import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Store, MapPin, Globe, Phone, Mail, Building2, User,
  ArrowRight, ArrowLeft, Loader2, Check, Sparkles, Info,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { ARENA } from '../lib/classTheme';
import { ArenaTitle, Laurel, GoldFrame } from '../components/ArenaFrame';
import CountryCityPicker from '../components/CountryCityPicker';
import { COUNTRIES, flagOf } from '../lib/countries';

const KINDS = [
  {
    key: 'local', icon: '🌱', label: 'Local business',
    sub: 'Shop, restaurant, salon — tied to one city',
    needsCity: true, needsAddress: true, needsWebsite: false,
  },
  {
    key: 'national', icon: '🏭', label: 'National brand',
    sub: 'Chain or brand present across the country',
    needsCity: false, needsAddress: false, needsWebsite: true,
  },
  {
    key: 'online_shop', icon: '🛒', label: 'Online shop',
    sub: 'E-commerce, ships anywhere',
    needsCity: false, needsAddress: false, needsWebsite: true,
  },
];

const PHONE_CODES = COUNTRIES
  .map(c => ({ code: c.code, dial: c.dial, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const STEPS = [
  { n: 1, label: 'Type', icon: Store },
  { n: 2, label: 'Business', icon: Building2 },
  { n: 3, label: 'Contact', icon: Phone },
];

export default function CreateShop() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [kind, setKind] = useState('local');
  const [loc, setLoc] = useState({});

  const [f, setF] = useState({
    name: '', description: '',
    website: '', address: '',
    contact_email: user?.email || '',
    phone_country: '+40', phone: '',
    contact_name: user?.display_name || '',
    best_call_time: '',
  });

  const selectedKind = KINDS.find(k => k.key === kind);

  const create = useMutation({
    mutationFn: () => api.post('/shops', {
      name: f.name,
      kind,
      description: f.description || undefined,
      website: f.website || undefined,
      address: selectedKind.needsAddress ? f.address || undefined : undefined,
      city_group_id: selectedKind.needsCity ? loc.cityGroupId : undefined,
      contact_email: f.contact_email,
      phone_country: f.phone_country,
      phone: f.phone,
      contact_name: f.contact_name || undefined,
      best_call_time: f.best_call_time || undefined,
    }),
    onSuccess: ({ data }) => {
      toast.success('Shop submitted for review! 🎉', { duration: 6000 });
      if (data.message) {
        toast(data.message, { duration: 8000, icon: '📞' });
      }
      nav(`/shop/${data.slug}`, { replace: true });
    },
    onError: e => toast.error(e.message),
  });

  /* ---------------- validation ---------------- */
  const step1Valid = !!kind;
  const step2Valid =
    f.name.trim().length >= 2 &&
    (!selectedKind.needsCity || !!loc.cityGroupId) &&
    (!selectedKind.needsWebsite || /^https?:\/\/.+/.test(f.website)) &&
    (!selectedKind.needsAddress || f.address.trim().length >= 3);
  const step3Valid =
    /\S+@\S+\.\S+/.test(f.contact_email) &&
    f.phone.replace(/\D/g, '').length >= 6;

  const canNext = step === 1 ? step1Valid : step === 2 ? step2Valid : step3Valid;

  return (
    <div className="max-w-lg mx-auto space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches p-5 flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
          style={{
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}`,
          }}>
          <Store size={22} style={{ color: ARENA.arenaText }} />
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <Laurel size={16} color={ARENA.arenaText} />
            <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]"
              style={{ color: ARENA.arenaText }}>Mercatura</span>
          </div>
          <h1 className="font-display text-xl font-extrabold neon-glow-text"
            style={{ color: ARENA.arenaText }}>
            Open your shop
          </h1>
          <p className="text-[11px] font-semibold" style={{ color: ARENA.arenaTextSoft }}>
            Free forever · loyal customers await
          </p>
        </div>
      </div>

      {/* ═══ PROGRESS ═══ */}
      <div className="flex items-center gap-1.5">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-center flex-1">
            <div className={`flex items-center gap-1.5`}
              style={{ color: step >= s.n ? ARENA.goldD : 'var(--text-muted)' }}>
              <div className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-black transition-all"
                style={{
                  background: step > s.n
                    ? 'linear-gradient(135deg,#34d399,#059669)'
                    : step === s.n ? ARENA.goldFrame : 'var(--s-200)',
                  color: step >= s.n ? '#fff' : 'var(--text-muted)',
                  boxShadow: step === s.n ? 'var(--shadow-glow)' : 'none',
                }}>
                {step > s.n ? <Check size={13} strokeWidth={3} /> : s.n}
              </div>
              <span className="text-[10px] font-display font-black uppercase tracking-wide hidden sm:block">
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-1.5 rounded transition-all"
                style={{ background: step > s.n ? '#34d399' : 'var(--s-200)' }} />
            )}
          </div>
        ))}
      </div>

      <div className="card p-5">
        <AnimatePresence mode="wait">
          {/* ══════════ STEP 1 — TYPE ══════════ */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 18 }} className="space-y-3">
              <p className="label">Business type</p>
              {KINDS.map(k => {
                const active = kind === k.key;
                return (
                  <button key={k.key} type="button" onClick={() => setKind(k.key)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition"
                    style={active
                      ? { borderColor: ARENA.gold, background: 'var(--accent-soft)' }
                      : { borderColor: 'var(--border)', background: 'var(--surface)' }}>
                    <span className="text-2xl shrink-0">{k.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm">{k.label}</p>
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{k.sub}</p>
                    </div>
                    {active && <Check size={16} strokeWidth={3} style={{ color: ARENA.goldD }} />}
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* ══════════ STEP 2 — BUSINESS DETAILS ══════════ */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 18 }} className="space-y-4">

              <div>
                <label className="label">Business name</label>
                <input className="input" value={f.name} maxLength={140}
                  placeholder="e.g. Golden Bakery"
                  onChange={e => setF({ ...f, name: e.target.value })} />
              </div>

              <div>
                <label className="label">Description (optional)</label>
                <textarea className="input min-h-[80px]" value={f.description} maxLength={500}
                  placeholder="What do you sell? What makes you special?"
                  onChange={e => setF({ ...f, description: e.target.value })} />
              </div>

              {/* ── LOCAL: country + city + address ── */}
              {selectedKind.needsCity && (
                <div>
                  <label className="label flex items-center gap-1.5">
                    <MapPin size={11} /> Location
                  </label>
                  <div className="rounded-xl p-3" style={{
                    background: 'var(--surface-alt)',
                    border: '1px solid var(--border)',
                  }}>
                    <CountryCityPicker value={loc} onChange={setLoc} />
                  </div>
                </div>
              )}

              {selectedKind.needsAddress && (
                <div>
                  <label className="label">Street address</label>
                  <input className="input" value={f.address} maxLength={255}
                    placeholder="e.g. 12 Main Street"
                    onChange={e => setF({ ...f, address: e.target.value })} />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Helps customers find your physical location
                  </p>
                </div>
              )}

              {/* ── NATIONAL / ONLINE: website ── */}
              {selectedKind.needsWebsite && (
                <div>
                  <label className="label flex items-center gap-1.5">
                    <Globe size={11} /> Website
                  </label>
                  <input className="input" type="url" value={f.website}
                    placeholder="https://www.example.com"
                    onChange={e => setF({ ...f, website: e.target.value })} />
                  {f.website && !/^https?:\/\/.+/.test(f.website) && (
                    <p className="text-[10px] mt-1 font-bold" style={{ color: ARENA.blood }}>
                      Must start with http:// or https://
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ══════════ STEP 3 — CONTACT ══════════ */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 18 }} className="space-y-4">

              <div className="p-3 rounded-xl flex gap-2.5"
                style={{ background: 'var(--accent-soft)', border: `1px solid ${ARENA.gold}44` }}>
                <Info size={16} style={{ color: ARENA.goldD }} className="shrink-0 mt-0.5" />
                <p className="text-[11px] font-semibold" style={{ color: 'var(--text-soft)' }}>
                  We'll contact you within 24 hours to set up your loyalty program
                  and send you a custom offer. This info stays private.
                </p>
              </div>

              <div>
                <label className="label flex items-center gap-1.5">
                  <Mail size={11} /> Contact email
                </label>
                <input className="input" type="email" value={f.contact_email}
                  placeholder="you@business.com"
                  onChange={e => setF({ ...f, contact_email: e.target.value })} />
              </div>

              <div>
                <label className="label flex items-center gap-1.5">
                  <Phone size={11} /> Phone number
                </label>
                <div className="flex gap-2">
                  <select className="input !w-32 shrink-0" value={f.phone_country}
                    onChange={e => setF({ ...f, phone_country: e.target.value })}>
                    {PHONE_CODES.map(c => (
                      <option key={c.code} value={c.dial}>
                        {flagOf(c.code)} {c.dial}
                      </option>
                    ))}
                  </select>
                  <input className="input flex-1" type="tel" value={f.phone}
                    placeholder="712 345 678"
                    onChange={e => setF({ ...f, phone: e.target.value.replace(/[^\d\s]/g, '') })} />
                </div>
              </div>

              <div>
                <label className="label flex items-center gap-1.5">
                  <User size={11} /> Contact person (optional)
                </label>
                <input className="input" value={f.contact_name} maxLength={120}
                  placeholder="Who should we ask for?"
                  onChange={e => setF({ ...f, contact_name: e.target.value })} />
              </div>

              <div>
                <label className="label">Best time to call (optional)</label>
                <input className="input" value={f.best_call_time} maxLength={60}
                  placeholder="e.g. Weekdays 10am–5pm"
                  onChange={e => setF({ ...f, best_call_time: e.target.value })} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ NAV ═══ */}
        <div className="flex gap-2 mt-6">
          {step > 1 && (
            <button type="button" onClick={() => setStep(s => s - 1)} className="btn-ghost">
              <ArrowLeft size={15} /> Back
            </button>
          )}

          {step < 3 ? (
            <button type="button" onClick={() => canNext && setStep(s => s + 1)} disabled={!canNext}
              className="btn-primary flex-1 py-3">
              Continue <ArrowRight size={15} />
            </button>
          ) : (
            <button type="button" onClick={() => create.mutate()}
              disabled={!canNext || create.isPending}
              className="btn-primary flex-1 py-3 font-display font-black">
              {create.isPending
                ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                : <><Sparkles size={16} /> Submit for review</>}
            </button>
          )}
        </div>
      </div>

      <p className="text-center text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
        Already have a shop? <Link to="/shops?mine=1" className="font-bold" style={{ color: ARENA.goldD }}>
          View my shops
        </Link>
      </p>
    </div>
  );
}